import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PageType } from '@vgtv/api-client/lib/page';

import { PageComponents } from '../components/PageComponents/PageComponents';
import { SearchBox } from '../components/SearchBox/SearchBox';
import { PageSpinner } from '../components/PageSpinner/PageSpinner';
import { useAppSelector } from '../modules/hooks';
import { selectSearchResults } from '../modules/search/selectors';
import {
  MQ_MOBILE_NAV,
  useMediaQuery,
} from '../components/utils/useMediaQuery';
import { usePageViewTracking } from '../components/utils/usePageViewTracking';
import { getPath, routeNames } from '../shared/helpers/routes';
import { isHeaderless } from '../utils/device';

import type { SearchComponent } from '../modules/search';
import type { Component } from '@vgtv/api-client/lib/component';
import type { FC } from 'react';

import * as styles from './SearchPage.module.scss';

const isNoResultsState = (components: SearchComponent[]) => {
  return components.every((component) => component.data.preset.length === 0);
};

export const SearchPage: FC = () => {
  const isMobile = useMediaQuery(MQ_MOBILE_NAV);
  const { t } = useTranslation();
  const { components } = useAppSelector(selectSearchResults);

  usePageViewTracking({
    pageType: PageType.CUSTOM,
    entity: { slug: getPath(routeNames.search).replace(/^\/+/g, '') },
  });

  let element;

  if (!components) {
    element = <PageSpinner />;
  } else if (isNoResultsState(components)) {
    element = (
      <div className={styles.noResults}>
        <h2>{t('We did not find what you were looking for')}</h2>
        <p>
          {t(
            'Check that all the words are spelled correctly, or try other keywords'
          )}
        </p>
      </div>
    );
  } else {
    element = <PageComponents componentsConfig={components as Component[]} />;
  }

  return (
    <div className={cn(styles.container, { [styles.isMobile]: isMobile })}>
      {isMobile && !isHeaderless ? null : (
        <div className={styles.searchBox}>
          <SearchBox variant={isMobile ? 'mobile' : 'desktop'} />
        </div>
      )}
      {element}
    </div>
  );
};
